import { Directive, booleanAttribute, computed, input } from '@angular/core';

/** How much room the control has. A dense header toggle is not a step in a form. */
export type ToggleSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * `pill` and `card` stand on their own and carry their own border; `segment` sits inside a bordered
 * group that draws the outline once for all of them.
 */
export type ToggleShape = 'pill' | 'card' | 'segment';

/** What the control reports while it is off — a count that needs answering tints its own button. */
export type ToggleTone = 'neutral' | 'warning';

/** Whether the control reads as one line or as a card that can carry a description under its label. */
export type ToggleLayout = 'inline' | 'block';

const BASE = 'transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none';

/** Inline controls sit in a row of their own kind; a block one is a choice the form is asking. */
const LAYOUT_CLASS = { inline: 'inline-flex gap-1 font-semibold', block: 'flex w-full gap-2 text-left' } as const;

const ALIGN_CLASS = { center: 'items-center', start: 'items-start' } as const;

const SIZE_CLASS: Record<ToggleSize, string> = {
  xs: 'px-2 py-0.5 text-xs',
  sm: 'px-2 py-0.5 text-sm',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-sm',
};

const SHAPE_CLASS: Record<ToggleShape, string> = {
  pill: 'rounded-full border',
  card: 'rounded-lg border',
  segment: 'rounded-full',
};

/**
 * On always reads as the accent, whatever the control was reporting while off. The fill carries the
 * state on its own: a segment draws no border of its own — the group draws one for all of them — so
 * a tint that only just parts from white leaves a chosen segment reading as an unchosen one.
 */
const PRESSED_CLASS: Record<ToggleShape, string> = {
  pill: 'border-primary-500 bg-primary-200 text-primary-900',
  card: 'border-primary-500 bg-primary-200 text-primary-900',
  segment: 'bg-primary-200 text-primary-900',
};

const RESTING_CLASS: Record<ToggleShape, Record<ToggleTone, string>> = {
  pill: {
    neutral: 'border-neutral-200 bg-neutral-0 text-neutral-600 hover:bg-neutral-50',
    warning: 'border-warning-300 bg-warning-50 text-warning-800 hover:bg-warning-100',
  },
  card: {
    neutral: 'border-neutral-200 bg-neutral-0 text-neutral-600 hover:bg-neutral-50',
    warning: 'border-warning-300 bg-warning-50 text-warning-800 hover:bg-warning-100',
  },
  segment: {
    neutral: 'text-neutral-500 hover:bg-neutral-50',
    warning: 'text-warning-800 hover:bg-warning-50',
  },
};

/** The container that draws one outline around a run of `segment` toggles. */
export const SEGMENTED_GROUP_CLASS = 'inline-flex shrink-0 overflow-hidden rounded-full border border-neutral-200';

/**
 * A control that is either on or off and says so — a facet, a view mode, a side panel.
 *
 * A directive rather than a component: every one of these carries its own label, icon and click
 * handler, and only the skin and the state are shared. It sets `aria-pressed` itself, so a toggle
 * cannot report a state to the page different from the one it draws.
 */
@Directive({
  selector: 'button[guaranteesToggle]',
  standalone: true,
  host: {
    '[class]': 'classes()',
    '[attr.aria-pressed]': 'menuTrigger() ? null : pressed()',
  },
})
export class ToggleDirective {
  pressed = input(false, { transform: booleanAttribute });
  size = input<ToggleSize>('md');
  shape = input<ToggleShape>('pill');
  /** Only read while the control is off. */
  tone = input<ToggleTone>('neutral');
  /**
   * The button opens a menu rather than switching something on, and only borrows the lit skin to
   * report that the menu holds an active choice. Its trigger already states `aria-expanded`, and a
   * button cannot be both pressed and expanded without lying about one of them.
   */
  menuTrigger = input(false, { transform: booleanAttribute });
  layout = input<ToggleLayout>('inline');
  /** `start` where the control's content runs to more than one line. */
  align = input<'center' | 'start'>('center');

  protected readonly classes = computed(() => {
    const shape = this.shape();
    const state = this.pressed() ? PRESSED_CLASS[shape] : RESTING_CLASS[shape][this.tone()];

    return `${BASE} ${LAYOUT_CLASS[this.layout()]} ${ALIGN_CLASS[this.align()]} ${SHAPE_CLASS[shape]} ${SIZE_CLASS[this.size()]} ${state}`;
  });
}
